// ─────────────────────────────────────────────
//  roomMessageBox — the Room Status screen's read-only message box.
//
//  PURE. No fetching, no Firestore, no React, no theme import (house rule —
//  see domain/conditions.js). Callers hand in the arrays they already
//  subscribe to; this module only normalises, joins and orders them.
//
//  Three buses already exist and none of them agree on field names
//  (2026-09-07 design §7.1):
//    • channel messages — `channels`/`channelMessages`, text lives in `body`,
//      time in `sentAt`. Scoped to CHANNEL_KINDS.perOr, scopeId = room.id.
//    • task directives  — `tasks`, text in `text`, time in `createdAt`.
//    • room alerts      — free text in `message`, time in `sentAt`.
//  Absorbing that spread is the whole point of having one mapper: a component
//  that reaches for `msg.text` on a channel doc renders a blank line and
//  nothing throws. Every field read here is deliberate, and every fallback is
//  commented.
//
//  ⚠️ THE BOX IS READ-ONLY (D5) AND IT IS NOT AN ALARM SURFACE. Nothing here
//  returns a severity, a tone or a blink. Which alerts belong in the box is the
//  CALLER's decision (AlertContext already has `conditionAlertsForRoom`); this
//  module only asks "is this alert for this room".
// ─────────────────────────────────────────────

// ── COPY ─────────────────────────────────────
// The exact wording of every non-ready state, in ONE place, because the
// provider page and the supervisor page both draw this box. Copied logic
// survives; copied VOCABULARY silently diverges — two pages that word "could
// not load" differently teach staff that the two screens mean different
// things. `ready` deliberately has no entry: the transcript is the content.
export const ROOM_MESSAGE_COPY = {
  uncovered: "Messages are not available for this campus.",
  loading: "Loading messages…",
  error: "Couldn't load messages.",
  empty: "No messages yet.",
  emptyDetail: "Board updates and notes from your MD appear here.",
};

// ── TIME ─────────────────────────────────────
// Firestore Timestamp | {seconds} | Date | epoch ms | anything → Date | null.
// Never throws: a cached/serialised doc, a half-arrived serverTimestamp and a
// device that lost the connection mid-snapshot all land here, and a message box
// that crashes sends a clinician to the telephone.
function toDate(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw?.toDate === "function") {
    try {
      const d = raw.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof raw?.toMillis === "function") {
    try {
      const d = new Date(raw.toMillis());
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === "number") {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Firestore's plain {seconds,nanoseconds} shape (cached / serialised docs).
  if (typeof raw?.seconds === "number") {
    const d = new Date(raw.seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // A date-ish STRING is not parsed on purpose. `new Date("yesterday")` is
  // Invalid, `new Date("07/09")` is a silent guess, and a guessed clock time on
  // a clinical transcript is worse than an absent one.
  return null;
}

// The three collections stamp three different fields, and a doc occasionally
// carries the generic `at` (a locally-composed line). First one that resolves
// wins; order per source so the collection's own field is always preferred.
const TIME_FIELDS = {
  message: ["sentAt", "createdAt", "at"],
  task: ["createdAt", "sentAt", "at"],
  alert: ["sentAt", "createdAt", "at"],
};

function timeOf(doc, source) {
  for (const field of TIME_FIELDS[source]) {
    const d = toDate(doc?.[field]);
    if (d) return d;
  }
  return null;
}

const clean = (v) => String(v ?? "").trim();

// ── NORMALISE ────────────────────────────────
// One line of the box. Returns null for anything unrenderable, so a caller
// never gets an empty bubble.
//
// `id` is NAMESPACED BY SOURCE (`task:abc`). Three collections are merged into
// one list that a component maps over, and two docs from different collections
// sharing an id would collide as React keys — which does not throw, it just
// drops or duplicates a line. The document id is `id.split(":")[1]`.
function toLine(doc, source, roomName, index) {
  if (!doc || typeof doc !== "object") return null;
  // Each collection's OWN text field first; the others are read as a fallback
  // only so a locally-composed line (or a doc that gains a field later) is not
  // silently blank.
  const text =
    source === "message" ? clean(doc.body || doc.text || doc.message)
    : source === "task" ? clean(doc.text || doc.body || doc.message)
    : clean(doc.message || doc.text || doc.body);
  if (!text) return null;
  const docId = clean(doc.id) || `idx-${index}`;
  return {
    id: `${source}:${docId}`,
    at: timeOf(doc, source),
    // Never invent an attribution. An unnamed sender renders as an unprefixed
    // line; "Unknown" in a message box reads like a person.
    from: clean(doc.senderName || doc.from),
    text,
    source,
    roomName,
  };
}

// ── THE TASK JOIN IS NAME-BASED, AND THAT IS A KNOWN WEAKNESS ────────────────
//
// ⚠️ `buildManualTask` writes `roomName`, NOT `roomId` (domain/tasks.js:188),
// so the only join available between a directive and a room is the room's
// display name. That name is typed by a human on the Timeline page and is
// re-imported from the daily sheet every morning; "WOR 4" vs "WOR4" is a real
// difference that no error surfaces — the directive simply never appears in the
// room's box. The design doc's §13 open item recommends adding `roomId` to
// `buildManualTask`; until that lands this comment IS the mitigation.
//
// What is defended here:
//   • case and surrounding whitespace, the two variants the same human types;
//   • the empty case. `"" === ""` is true, so a task with no roomName would
//     otherwise match EVERY room, and a room with no name would collect EVERY
//     task on the campus — a directive for another room appearing in this one's
//     box is the failure mode that gets a patient in the wrong place.
// Anything beyond that (punctuation, "OR4" vs "WOR4") is NOT guessed: a fuzzy
// room match is a silently wrong room.
function roomNamesMatch(a, b) {
  const left = clean(a).toLowerCase();
  const right = clean(b).toLowerCase();
  if (!left || !right) return false;
  return left === right;
}

// Ascending by time, NEWEST LAST — the box reads as a transcript.
//
// ⚠️ AN UNDATED LINE SORTS TO THE OLDEST END AND IS MARKED `at: null`. It is
// never treated as "now". A serverTimestamp is null on the local echo of a
// write, and a serialised doc can lose its stamp entirely; treating that as the
// current time would put an unknown line in the slot a clinician reads as the
// latest instruction. Sorting it old and stamping it `null` lets the UI label
// it ("time unknown") instead of fabricating one — the absence is explicit, not
// silent. Compare with domain/tasks.js `createdMs`, which sorts an undated task
// FIRST for the opposite and equally deliberate reason: there the list is
// newest-first and the undated doc is the one the user just wrote.
//
// The comparator returns -1/0/1 rather than a subtraction. Two undated lines
// under a subtraction would be `-Infinity - -Infinity` = NaN, which makes
// Array.sort's ordering implementation-defined — the classic way a "does not
// crash" sort still scrambles a list.
function sortOldestFirst(lines) {
  return lines
    .map((line, index) => ({ line, index }))
    .sort((a, b) => {
      const at = a.line.at ? a.line.at.getTime() : null;
      const bt = b.line.at ? b.line.at.getTime() : null;
      if (at !== bt) {
        if (at === null) return -1;
        if (bt === null) return 1;
        return at < bt ? -1 : 1;
      }
      return a.index - b.index; // stable: equal stamps keep arrival order
    })
    .map((x) => x.line);
}

/**
 * Merge the room's three message buses into one ordered transcript.
 *
 * @param {object}  input
 * @param {Array}   input.channelMessages  perOr channel docs, ALREADY scoped to
 *   this room's channel by the caller (the channel id embeds facilityId and
 *   room id — channels.js:42 — so a foreign room can never match one).
 * @param {Array}   input.tasks   today's directives for the facility (unscoped).
 * @param {Array}   input.alerts  room alerts (the caller picks which types).
 * @param {object}  input.room    { id, name }
 * @returns {Array<{id:string, at:Date|null, from:string, text:string,
 *   source:"message"|"task"|"alert", roomName:string}>} oldest first.
 */
export function normaliseRoomMessages({
  channelMessages = [],
  tasks = [],
  alerts = [],
  room = null,
} = {}) {
  const roomId = clean(room?.id);
  const roomName = clean(room?.name);
  const list = (v) => (Array.isArray(v) ? v : []);

  const lines = [];

  // 1. Channel messages — pass through. Membership is derived from the LIVE
  //    assignment (MessagingContext), so filtering again here on a stale room
  //    doc would drop the relieving CRNA's own messages.
  list(channelMessages).forEach((doc, i) => {
    const line = toLine(doc, "message", roomName || clean(doc?.roomName), i);
    if (line) lines.push(line);
  });

  // 2. Tasks — name join (see roomNamesMatch). Displayed under the ROOM's
  //    canonical name, not the task's, so "wor4" and "WOR4" do not render as
  //    two different rooms in a merged supervisor box.
  list(tasks).forEach((doc, i) => {
    if (!roomNamesMatch(doc?.roomName, roomName)) return;
    const line = toLine(doc, "task", roomName, i);
    if (line) lines.push(line);
  });

  // 3. Alerts — roomId join. Reliable, and the reason the design recommends
  //    giving tasks a roomId too.
  list(alerts).forEach((doc, i) => {
    const alertRoomId = clean(doc?.roomId);
    if (!alertRoomId || !roomId || alertRoomId !== roomId) return;
    const line = toLine(doc, "alert", roomName || clean(doc?.roomName), i);
    if (line) lines.push(line);
  });

  return sortOldestFirst(lines);
}

// ── STATE ────────────────────────────────────
/**
 * Which of the box's five renderings applies. The UI draws each one
 * differently (§7.2) and this is the ONLY function that tells them apart.
 *
 * ⚠️ THE ORDER IS THE SAFETY PROPERTY, AND IT FAILS CLOSED.
 *
 *  1. uncovered — the messaging bus is following a different facility. Wins
 *     over everything, including a non-empty `messages`: those lines belong to
 *     another campus, and a reassuring "No messages yet." for a room the app
 *     cannot actually hear is a lie a clinician acts on. Mirrors
 *     `alertsCoverFacility()` (AlertContext.js), which fails closed for the
 *     same reason.
 *  2. loading  — the read is still in flight; nothing is known yet.
 *  3. error    — INCLUDING a degraded bus. `useChannelMessages` gives up after
 *     5 retries and leaves `{ [channelId]: [] }` behind, which is byte-for-byte
 *     what a quiet channel looks like. It beats `ready` on purpose: a partial
 *     list rendered as a complete one is the same lie in a smaller font.
 *  4. empty    — covered, loaded, error-free and genuinely nothing to say.
 *  5. ready    — draw the transcript.
 *
 * `covered` DEFAULTS TO FALSE. A caller that forgets to pass it gets a visible
 * refusal, which is reported; the alternative default gets a confident empty
 * box on a foreign campus, which is not.
 *
 * @returns {"uncovered"|"loading"|"error"|"empty"|"ready"}
 */
export function roomMessageBoxState({
  covered = false,
  degraded = false,
  error = null,
  loading = false,
  messages = [],
} = {}) {
  if (covered !== true) return "uncovered";
  if (loading) return "loading";
  if (error || degraded) return "error";
  if (!Array.isArray(messages) || messages.length === 0) return "empty";
  return "ready";
}
